import { Grid, useBreakpointValue, VStack } from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import useIsAboveViewportHeight from '../../../../../common/src/hooks/useIsAboveViewportHeight';
import QuestionGroupActions from '../../../../../flows/src/components/ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import useSessionId from '../../../../../flows/src/hooks/useSessionId';
import { useRiskObjectsByParty } from '../../../../../parties/src/features/risk-objects/api/getAllRiskObjectByParty';
import AssistantQuestion from '../../../components/flow/AssistantQuestion';
import ScrollableContentContainer from '../../../components/layout/ScrollableContentContainer';
import ViewHeader from '../../../components/legacy/question-view-header/QuestionViewHeader';
import { useFlowSessionObject } from '../../../hooks/useFlowQueries';
import { resetOffers } from '../../../store/redux/slices/offersSlice';
import {
  useLouiseDispatch,
  useLouiseSelector,
} from '../../../store/redux/store';
import { usePatchRiskObjectSelection } from '../api/patchRiskObjectSelection';
import RiskObjectSelectOverview from '../components/RiskObjectSelectOverview';

const ChooseRiskObject: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const sessionId = useSessionId();
  const above480px = useIsAboveViewportHeight('vga');
  const isMobile = useBreakpointValue({ base: true, laptop: false });
  const history = useHistory();
  const { data: session, isLoading } = useFlowSessionObject(sessionId);
  const { data: riskObjects, isLoading: isLoadingRiskObjects } =
    useRiskObjectsByParty(session?.prospect.id);
  const selectedRiskObjects =
    useLouiseSelector((state) => state.offers.chosenRiskObjectsForTarif) || {};
  const {
    mutateAsync: patchRiskObjectSelection,
    isLoading: isLoadingPatchRiskObjectSelection,
  } = usePatchRiskObjectSelection();
  const louiseDispatch = useLouiseDispatch();

  const getDefinedRiskObjects = () => {
    const definedRiskObjects: string[] = [];
    if (riskObjects) {
      Object.entries(riskObjects).forEach(([key, value]) => {
        if (value.length > 1) {
          definedRiskObjects.push(key);
        }
      });
    }
    return definedRiskObjects;
  };

  const areAllObjectsSelected =
    getDefinedRiskObjects().length === Object.keys(selectedRiskObjects).length;

  const handleBack = () => {
    history.push(`/product/session/finalize?session_id=${sessionId}`);
  };

  const handleContinue = async () => {
    await patchRiskObjectSelection({
      sessionId,
      riskObjectIds: Object.values(selectedRiskObjects),
    });

    // Removes all queries related to this session
    // If query cache is ever persisted this should be moved to the function call
    // when a new session is created
    queryClient.removeQueries([sessionId, 'quotes']);
    queryClient.removeQueries([sessionId, 'products']);
    louiseDispatch(resetOffers());

    history.push(`/product/session/offers?session_id=${sessionId}`);
  };

  return (
    <Grid
      height="100%"
      width="100%"
      templateRows={{
        base: above480px ? 'auto minmax(1px, 1fr) auto' : 'repeat(3, auto)',
        laptop: 'minmax(1px, 1fr) auto',
      }}
      background="#fdfcff"
    >
      {isMobile && <ViewHeader />}
      <ScrollableContentContainer>
        <AssistantQuestion
          question={t(
            'SALES-CONVERSATION_FINALIZE_CHOOSE-RIKS-OBJECTS_ASSISTANT_TITLE',
          )}
          description={t(
            'SALES-CONVERSATION_FINALIZE_CHOOSE-RIKS-OBJECTS_ASSISTANT_DESCRIPTION',
          )}
        />
        <VStack align="stretch" w="100%" maxW="3xl" alignSelf="center">
          <RiskObjectSelectOverview
            isLoading={isLoading || isLoadingRiskObjects}
            riskObjects={riskObjects}
          />
        </VStack>
      </ScrollableContentContainer>
      <QuestionGroupActions
        onBack={handleBack}
        onContinue={handleContinue}
        disabledContinue={!areAllObjectsSelected}
        isLoading={isLoadingPatchRiskObjectSelection}
        noSkip
      />
    </Grid>
  );
};

export default ChooseRiskObject;
